<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div>
        <h5 class="fw-bold mb-2">❌ 카드 결제 실패 내역</h5>
        <div class="btn-group btn-group-sm" role="group">
          <router-link to="/admin/payment-result" class="btn btn-outline-primary">결제 내역</router-link>
          <router-link to="/admin/payment-cancel" class="btn btn-outline-primary">카드 취소 내역</router-link>
          <button type="button" class="btn btn-primary active">결제 실패 내역</button>
        </div>
      </div>
      <span class="text-muted small">총 {{ pagination.total }}건</span>
    </div>

    <!-- 검색 -->
    <div class="row g-2 mb-3">
      <div class="col-auto">
        <div class="input-group">
          <input 
            v-model="searchCarNum" 
            type="text" 
            class="form-control form-control-sm" 
            placeholder="차량번호 입력 (3444 형식)" 
            style="width: 200px;"
            @keyup.enter="performSearch"
          />
          <button class="btn btn-sm btn-danger" @click="performSearch" :disabled="loading">
            {{ loading ? '검색 중...' : '검색' }}
          </button>
          <button class="btn btn-sm btn-outline-secondary" @click="resetSearch">초기화</button>
        </div>
      </div>
      <div class="col-auto">
        <select v-model="currentLimit" class="form-select form-select-sm" style="width: 100px;">
          <option :value="10">10개</option>
          <option :value="20">20개</option>
          <option :value="50">50개</option>
        </select>
      </div>
    </div>

    <!-- 테이블 -->
    <div class="table-responsive">
      <table class="table table-sm table-hover border">
        <thead class="table-light sticky-top">
          <tr>
            <th class="text-center">ID</th>
            <th class="text-center">Tid</th>
            <th class="text-center">구매자명</th>
            <th class="text-center">차량번호</th>
            <th class="text-center">결제금액</th>
            <th class="text-center">결제수단</th>
            <th class="text-center">결과코드</th>
            <th class="text-center">결과메시지</th>
            <th class="text-center">상품명</th>
            <th class="text-center">주차장</th>
            <th class="text-center">연락처</th>
            <th class="text-center">이메일</th>
            <th class="text-center">등록일시</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="results.length === 0">
            <td colspan="13" class="text-center text-muted py-4">결제 실패 내역이 없습니다.</td>
          </tr>
          <tr v-for="item in results" :key="item.ID" class="align-middle">
            <td class="text-center small">{{ item.ID }}</td>
            <td class="text-center small">{{ item.TID || '-' }}</td>
            <td class="text-center">{{ item.BUYER_NAME || '-' }}</td>
            <td class="text-center fw-bold">{{ item.CAR_NUM || '-' }}</td>
            <td class="text-center text-danger fw-bold">{{ item.AMT ? item.AMT.toLocaleString() : '-' }} 원</td>
            <td class="text-center small">{{ item.PAY_METHOD_NAME || item.PAY_METHOD || '-' }}</td>
            <td class="text-center">
              <span :class="{
                'badge bg-danger': item.RESULT_CODE && item.RESULT_CODE !== '0000',
                'text-muted': !item.RESULT_CODE
              }">
                {{ item.RESULT_CODE || '-' }}
              </span>
            </td>
            <td class="text-center small" :title="item.RESULT_MSG">{{ truncate(item.RESULT_MSG, 20) || '-' }}</td>
            <td class="text-center small">{{ item.GOODS_NAME || '-' }}</td>
            <td class="text-center small">{{ item.AreaSeq || item.AREA_ID || '-' }}</td>
            <td class="text-center small">{{ item.BUYER_TEL || '-' }}</td>
            <td class="text-center small">{{ item.BUYER_EMAIL || '-' }}</td>
            <td class="text-center small">{{ formatDate(item.REG_DATE) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 페이징 -->
    <nav v-if="pagination.total > 0" aria-label="Page navigation" class="mt-3">
      <ul class="pagination justify-content-center">
        <li :class="{ 'page-item disabled': currentOffset === 0 }">
          <button class="page-link" @click="previousPage">이전</button>
        </li>
        <li class="page-item active">
          <span class="page-link">{{ currentPage }} / {{ totalPages }}</span>
        </li>
        <li :class="{ 'page-item disabled': !pagination.hasMore }">
          <button class="page-link" @click="nextPage" :disabled="!pagination.hasMore">다음</button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import axios from 'axios'

const SERVER_URL = process.env.VUE_APP_API_BASE

const results = ref([])
const loading = ref(false)
const searchCarNum = ref('')
const isSearching = ref(false)
const currentOffset = ref(0)
const currentLimit = ref(20)
const pagination = reactive({
  total: 0,
  limit: 20,
  offset: 0,
  hasMore: false
})

const currentPage = computed(() => Math.floor(currentOffset.value / currentLimit.value) + 1)
const totalPages = computed(() => Math.ceil(pagination.total / currentLimit.value) || 1)

async function fetchPaymentFailResults() {
  loading.value = true
  try {
    const params = {
      limit: currentLimit.value,
      offset: currentOffset.value
    }

    let url = `${SERVER_URL}/api/admin/payment-fail-result`
    
    if (isSearching.value && searchCarNum.value) {
      url = `${SERVER_URL}/api/admin/payment-fail-result/search`
      params.carNum = searchCarNum.value
    }

    const response = await axios.get(url, { params })
    
    if (response.data.success) {
      results.value = response.data.data || []
      pagination.total = response.data.pagination.total
      pagination.limit = response.data.pagination.limit
      pagination.offset = response.data.pagination.offset
      pagination.hasMore = response.data.pagination.hasMore
      console.log('[PaymentFailResult] 조회 완료:', results.value.length, '건')
    } else {
      alert(response.data.message || '조회에 실패했습니다.')
    }
  } catch (e) {
    console.error('[PaymentFailResult] 조회 오류:', e)
    alert('조회 중 오류가 발생했습니다.')
  } finally {
    loading.value = false
  }
}

function performSearch() {
  if (!searchCarNum.value.trim()) {
    alert('차량번호를 입력하세요.')
    return
  }
  isSearching.value = true
  currentOffset.value = 0
  fetchPaymentFailResults()
}

function resetSearch() {
  searchCarNum.value = ''
  isSearching.value = false
  currentOffset.value = 0
  fetchPaymentFailResults()
}

function previousPage() {
  if (currentOffset.value >= currentLimit.value) {
    currentOffset.value -= currentLimit.value
    fetchPaymentFailResults()
  }
}

function nextPage() {
  if (pagination.hasMore) {
    currentOffset.value += currentLimit.value
    fetchPaymentFailResults()
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  try {
    const date = new Date(dateStr)
    return date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
  } catch (e) {
    return dateStr
  }
}

function truncate(str, length) {
  if (!str) return ''
  if (str.length > length) return str.substring(0, length) + '...'
  return str
}

onMounted(() => {
  fetchPaymentFailResults()
})
</script>

<style scoped>
.table {
  font-size: 0.85rem;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #dee2e6;
}

.table-hover tbody tr:hover {
  background-color: #f5f5f5;
}

.badge {
  font-weight: 500;
  padding: 4px 8px;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 0.8rem;
}
</style>
